import React, { Component, Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { orderBy, has, set, get, split } from 'lodash/fp';
import { Table, Icon, IconButton } from 'rsuite';
import { toastr } from 'react-redux-toastr';
import Pagination from '../../Pagination';
import DateCell from '../../widgets/custom-cell/Date';
import * as PERMISSIONS from '../../../constants/permissions/guest';
import redirectTo from '../../../helpers/redirectTo';
import { fullAccess, viewAccess } from '../../../helpers/permission';
import { ClientId, ClientDomainId, AppConfig } from '../../../helpers/config';
import { awsS3presignedRequest } from '../../../services/fbm-api';
import AccessControl from '../../AccessControl';
import PaginationType from '../../../interfaces/PaginationType';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import DeleteConfirmationModal from '../../DeleteConfirmationModal';
import { FileType } from '../../../interfaces';
import AutoHeightTable from '../../AutoHeightTable';

const { Column, HeaderCell, Cell } = Table;

const ActionCell = ({
  rowData,
  dataKey,
  handleDelete,
  handleView,
  ...props
}) => {
  const [viewFileId, setViewFileId] = useState(null);
  const loadingForView = rowData.id === viewFileId;
  return (
    <Cell {...props} className="link-group">
      <AccessControl allowOnlyFor={viewAccess(PERMISSIONS.MODULE)}>
        <IconButton
          appearance="link"
          style={{ top: '-8px' }}
          color="blue"
          icon={
            loadingForView ? <Icon icon="spinner" spin /> : <Icon icon="eye" />
          }
          disabled={loadingForView}
          onClick={async () => {
            setViewFileId(rowData.id);
            await handleView(rowData);
            setViewFileId(null);
          }}
        />
      </AccessControl>
      <AccessControl allowOnlyFor={fullAccess(PERMISSIONS.MODULE)}>
        <IconButton
          style={{ top: '-8px' }}
          appearance="link"
          color="red"
          onClick={() => handleDelete(rowData)}
          icon={<Icon icon="trash" />}
        />
      </AccessControl>
    </Cell>
  );
};
ActionCell.propTypes = {
  rowData: FileType,
  dataKey: PropTypes.string.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleView: PropTypes.func.isRequired
};
ActionCell.defaultProps = {
  rowData: null
};

class GuestFileList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fileToDelete: null,
      showConfirmDelete: false
    };
  }

  componentDidMount() {
    const { onInit, isFetched } = this.props;
    if (!isFetched) {
      onInit();
    }
  }

  componentDidUpdate({ isDeleted: prevIsDeleted }) {
    const { isDeleted } = this.props;
    if (!prevIsDeleted && isDeleted) {
      this.handleCloseDeleteModal();
    }
  }

  getData() {
    const { files } = this.props;
    const { sortColumn, sortType } = this.state;
    return orderBy(sortColumn, sortType, files);
  }

  handleSortColumn = (sortColumn, sortType) => {
    const { onSort, query } = this.props;
    onSort(sortColumn, sortType, query);
  };

  handleViewFile = async ({ fileName, objId, objType }) => {
    const isMergedFile = fileName.substr(0, 6) === 'guest/';
    const imagePath = isMergedFile
      ? fileName
      : `${objType}/${objId}/${fileName}`;
    const bucket = get('buckets.files', AppConfig);
    const baseDir = `client-${ClientId}/domain-${ClientDomainId}`;
    const body = { bucket, filename: imagePath, method: 'get', baseDir };
    const response = await awsS3presignedRequest({ body });
    if (has('url', response)) {
      redirectTo(response.url, true);
    } else {
      toastr.error('Error', response.message);
    }
  };

  handleOpenDeleteModal = fileToDelete => {
    this.setState({ showConfirmDelete: true, fileToDelete });
  };

  handleCloseDeleteModal = () => {
    this.setState({ showConfirmDelete: false });
  };

  handlConfirmDelete = () => {
    const { fileToDelete } = this.state;
    const { onDelete } = this.props;
    onDelete(fileToDelete.id);
  };

  handleChangePage = page => {
    const { onChangePage, loadedPages, query } = this.props;
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  handleChangeLimit = limit => {
    const { onChangeLimit, query } = this.props;
    const newQuery = { ...query, limit, page: 1 };
    onChangeLimit(newQuery);
  };

  render() {
    const { isFetching, isDeleting, query, pagination } = this.props;
    const [sortColumn, sortType] = split(':', get('order', query));
    const { fileToDelete, showConfirmDelete } = this.state;

    return (
      <Fragment>
        <AutoHeightTable
          reduceHeight={540}
          loading={isFetching}
          data={this.getData()}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={this.handleSortColumn}
          bordered
        >
          <Column width={60} align="center" fixed sortable>
            <CustomHeaderCell>Id</CustomHeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column width={180} fixed sortable>
            <CustomHeaderCell>Name</CustomHeaderCell>
            <Cell dataKey="label" />
          </Column>
          <Column width={140} sortable>
            <CustomHeaderCell>Date Created</CustomHeaderCell>
            <DateCell dataKey="createdAt" />
          </Column>
          <Column width={100}>
            <HeaderCell align="center">Action</HeaderCell>
            <ActionCell
              align="center"
              dataKey="id"
              handleDelete={this.handleOpenDeleteModal}
              handleView={this.handleViewFile}
            />
          </Column>
        </AutoHeightTable>

        {fileToDelete && (
          <DeleteConfirmationModal
            message={`Are you sure you want to delete file "${
              fileToDelete.label
            }"?`}
            show={showConfirmDelete}
            onHide={this.handleCloseDeleteModal}
            onConfirm={this.handlConfirmDelete}
            isDeleting={isDeleting}
          />
        )}

        <Pagination
          page={pagination.currentPage}
          limit={pagination.limit}
          total={pagination.totalCount}
          onChangePage={this.handleChangePage}
          onChangeLimit={this.handleChangeLimit}
        />
      </Fragment>
    );
  }
}

GuestFileList.propTypes = {
  files: PropTypes.arrayOf(FileType).isRequired,
  isFetching: PropTypes.bool.isRequired,
  isFetched: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  isDeleted: PropTypes.bool.isRequired,
  onInit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired
};

export default GuestFileList;
